/**
 * The group menu — exact order from `[ui:library/table-14-group-header-menu.png]`
 * (spec M1-B2 §8.2). `Delete` before `Archive` is monday's order in this build; do not "fix" it.
 * `Apps ▸` is deliberately **not rendered** (no board apps in this product).
 */
import type { GroupId } from '@elizmonday/shared';
import { GROUP_COLORS } from '@elizmonday/shared';
import { useTable, useTableActions } from '../context';
import { Menu, type MenuEntry } from './Menu';
import { downloadCsv, safeFilename, toCsv } from './export-rows';

/** "Rename group" hands the caret to the header's inline editor; the menu owns no input. */
export const GROUP_RENAME_EVENT = 'elizmonday:rename-group';

export function GroupMenu({ groupId, x, y }: { groupId: GroupId; x: number; y: number }) {
  const payload = useTable((state) => state.payload);
  const actions = useTableActions();
  const group = payload?.groups.find((entry) => entry.id === groupId);
  if (!group) return null;

  const hasSubitems = (payload?.columns ?? []).some((column) => column.type === 'subtasks');

  const entries: MenuEntry[] = [
    {
      id: 'collapse',
      label: 'Collapse this group',
      onSelect: () => void actions.toggleGroupCollapsed(groupId, !group.collapsed),
    },
    {
      id: 'collapse-all',
      label: 'Collapse all groups',
      onSelect: () => void actions.toggleGroupCollapsed('all', true),
    },
    {
      id: 'select-all',
      label: 'Select all items in group',
      onSelect: () => {
        const ids = group.page.items.map((item) => item.id);
        actions.setItemSelection({ ids, anchor: ids[ids.length - 1] ?? null });
      },
    },
    { id: 'expand-subitems', label: 'Expand all subitems', disabled: !hasSubitems },
    { id: 'collapse-subitems', label: 'Collapse all subitems', disabled: !hasSubitems },
    { id: 'd1', label: '', divider: true },
    {
      id: 'add-group',
      label: 'Add group',
      // monday inserts the new group **above** the one whose menu is open.
      onSelect: () => {
        actions.openMenu(null);
        void actions.addGroup(undefined, { method: 'before_at', relativeTo: String(groupId) });
      },
    },
    {
      id: 'duplicate',
      label: 'Duplicate this group',
      /*
       * **P-058.** monday's submenu is two rows, `Duplicate items` and `Duplicate items &
       * updates` (`[c:group-menu-duplicate]`, 248 × 32 at 650,478 / 650,510). Round 3 modelled
       * it on `groups.duplicate`'s `withItems` flag instead and invented both labels: it
       * offered a *Structure only* row monday does not have and was missing the one that
       * copies the updates. Whether monday has a structure-only path anywhere is **q485**, so
       * it stays out of the menu until a capture says otherwise.
       */
      submenu: [
        {
          id: 'duplicate-items',
          label: 'Duplicate items',
          onSelect: () => {
            actions.openMenu(null);
            void actions.duplicateGroup(groupId, true, false);
          },
        },
        {
          id: 'duplicate-items-updates',
          label: 'Duplicate items & updates',
          onSelect: () => {
            actions.openMenu(null);
            void actions.duplicateGroup(groupId, true, true);
          },
        },
      ],
    },
    {
      id: 'move',
      label: 'Move group',
      submenu: [
        {
          id: 'move-top',
          label: 'To the top',
          disabled: (payload?.groups[0]?.id ?? null) === groupId,
          onSelect: () => {
            actions.openMenu(null);
            void actions.moveGroup(groupId, 'top');
          },
        },
        {
          id: 'move-bottom',
          label: 'To the bottom',
          disabled: (payload?.groups[payload.groups.length - 1]?.id ?? null) === groupId,
          onSelect: () => {
            actions.openMenu(null);
            void actions.moveGroup(groupId, 'bottom');
          },
        },
      ],
    },
    { id: 'd2', label: '', divider: true },
    {
      id: 'rename',
      label: 'Rename group',
      onSelect: () => {
        actions.openMenu(null);
        // The header's own inline editor owns the rename; the menu just opens it.
        window.dispatchEvent(
          new CustomEvent(GROUP_RENAME_EVENT, { detail: { groupId: String(groupId) } }),
        );
      },
    },
    {
      id: 'colour',
      label: 'Change group color',
      /** The 23-colour group palette `packages/shared/src/groups` owns `[B2§8.1]`. */
      submenu: GROUP_COLORS.map((color) => ({
        id: `colour-${color}`,
        label: color,
        onSelect: () => void actions.setGroupColor(groupId, color),
      })),
    },
    { id: 'd3', label: '', divider: true },
    {
      /**
       * B10. `[c:group-menu]` confirms the entry and its position. B9's `export.board` takes
       * a board and nothing else, so the group scope is written client-side as CSV from the
       * rows already loaded — a registered deviation (`export-rows.ts`), not a greyed entry.
       */
      id: 'export',
      label: 'Export to Excel',
      onSelect: () => {
        actions.openMenu(null);
        downloadCsv(
          safeFilename(payload?.board.name ?? 'Board', group.title),
          toCsv(payload?.columns ?? [], group.page.items, payload?.board.itemTerminology ?? 'Name'),
        );
      },
    },
    { id: 'd4', label: '', divider: true },
    {
      id: 'delete',
      label: 'Delete group',
      danger: true,
      onSelect: () => {
        actions.openMenu(null);
        void actions.deleteGroup(groupId);
      },
    },
    {
      id: 'archive',
      label: 'Archive group',
      onSelect: () => {
        actions.openMenu(null);
        void actions.archiveGroup(groupId);
      },
    },
  ];

  return (
    <Menu
      entries={entries}
      x={x}
      y={y}
      label={`${group.title} options`}
      onClose={() => actions.openMenu(null)}
    />
  );
}
